import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { translations } from '../lib/translations';
import { ArrowLeft, Plus, House, PencilSimple, Trash, MapPin, Bed, Buildings, Door } from '@phosphor-icons/react';
import { toast } from 'sonner';
import IcalSection from '../components/IcalSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PropertiesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [saving, setSaving] = useState(false);

  const lang = user?.preferred_language || 'ITA';
  const t = translations[lang];

  const emptyForm = {
    name: '',
    property_city: '',
    property_province: '',
    property_address: '',
    property_number: '',
    cadastral_sheet: '',
    cadastral_parcel: '',
    cadastral_sub: '',
    cin_code: '',
    max_occupants: 4,
    pets_allowed: false,
    foro_competente: '',
    property_type: 'appartamento',
    rooms_setup: [],
  };

  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`, { withCredentials: true });
      setProperties(response.data);
    } catch (err) {
      console.error('Error fetching properties:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (saving) return;
    if (!formData.name || !formData.property_city || !formData.property_address) {
      toast.error('Compila i campi obbligatori');
      return;
    }

    setSaving(true);
    try {
      if (editingProperty) {
        await axios.put(`${API}/properties/${editingProperty.id}`, formData, { withCredentials: true });
        toast.success('Alloggio aggiornato');
      } else {
        await axios.post(`${API}/properties`, formData, { withCredentials: true });
        toast.success('Alloggio creato');
      }
      setIsDialogOpen(false);
      setEditingProperty(null);
      setFormData(emptyForm);
      fetchProperties();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvare');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (property) => {
    setEditingProperty(property);
    // Pre-fill form
    const isStanze = property.property_type && property.property_type !== 'appartamento';
    let rooms_setup = [];
    if (isStanze) {
      try {
        const r = await axios.get(`${API}/cleaning/rooms?property_id=${property.id}`, { withCredentials: true });
        rooms_setup = (r.data || []).map(rm => ({ name: rm.name, max_occupancy: rm.max_occupancy }));
      } catch (e) { /* ignore */ }
    }
    setFormData({
      name: property.name,
      property_city: property.property_city,
      property_province: property.property_province,
      property_address: property.property_address,
      property_number: property.property_number,
      cadastral_sheet: property.cadastral_sheet,
      cadastral_parcel: property.cadastral_parcel,
      cadastral_sub: property.cadastral_sub,
      cin_code: property.cin_code,
      max_occupants: property.max_occupants,
      pets_allowed: property.pets_allowed,
      foro_competente: property.foro_competente || '',
      property_type: property.property_type || 'appartamento',
      rooms_setup,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (propertyId) => {
    if (!window.confirm('Eliminare questo alloggio?')) return;
    try {
      await axios.delete(`${API}/properties/${propertyId}`, { withCredentials: true });
      toast.success('Alloggio eliminato');
      fetchProperties();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleNew = () => {
    setEditingProperty(null);
    setFormData(emptyForm);
    setIsDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-zinc-100 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={24} weight="bold" className="text-zinc-700" />
          </button>
          <div>
            <h1 className="text-xl font-black text-zinc-900 tracking-tight">I Miei Alloggi</h1>
            <p className="text-sm text-zinc-500">Gestisci le tue strutture turistiche</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Actions */}
        <div className="flex justify-between items-center mb-6">
          <p className="text-zinc-500">{properties.length} {properties.length === 1 ? 'alloggio' : 'alloggi'}</p>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={handleNew}
                className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider"
                data-testid="new-property-button"
              >
                <Plus size={20} className="mr-2" />
                Nuovo Alloggio
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl font-black">
                  {editingProperty ? 'Modifica Alloggio' : 'Nuovo Alloggio'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-5 py-4">
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">
                    Nome Struttura *
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="es. Appartamento Centro Storico"
                    className="rounded-none mt-2"
                    data-testid="property-name-input"
                  />
                </div>

                {/* Tipologia struttura — guida il modulo Pulizie */}
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">
                    Tipo di struttura *
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                    {[
                      { id: 'appartamento', label: 'Appartamento', desc: 'Casa intera per ospite', icon: House },
                      { id: 'bnb', label: 'B&B', desc: 'Più stanze · servizio prima colazione', icon: Buildings },
                      { id: 'affittacamere', label: 'Affittacamere', desc: 'Più stanze indipendenti', icon: Door },
                    ].map(opt => {
                      const active = formData.property_type === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => handleChange('property_type', opt.id)}
                          className={`text-left p-3 border-2 transition-all ${active ? 'border-emerald-600 bg-emerald-50' : 'border-zinc-200 hover:border-zinc-400'}`}
                          data-testid={`property-type-${opt.id}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <opt.icon size={16} weight={active ? 'fill' : 'bold'} className={active ? 'text-emerald-700' : 'text-zinc-400'} />
                            <p className={`text-sm font-black ${active ? 'text-emerald-700' : 'text-zinc-900'}`}>{opt.label}</p>
                          </div>
                          <p className="text-[11px] text-zinc-500 leading-snug">{opt.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-1.5">
                    Per B&amp;B e affittacamere puoi configurare le singole stanze qui sotto. Saranno usate automaticamente nel modulo Pulizie.
                  </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.propertyCity} *</Label>
                    <Input
                      value={formData.property_city}
                      onChange={(e) => handleChange('property_city', e.target.value)}
                      className="rounded-none mt-2"
                      data-testid="property-city-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.propertyProvince}</Label>
                    <Input
                      value={formData.property_province}
                      onChange={(e) => handleChange('property_province', e.target.value.toUpperCase())}
                      maxLength={2}
                      className="rounded-none mt-2"
                      data-testid="property-province-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  <div className="sm:col-span-3">
                    <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.propertyAddress} *</Label>
                    <Input
                      value={formData.property_address}
                      onChange={(e) => handleChange('property_address', e.target.value)}
                      className="rounded-none mt-2"
                      data-testid="property-address-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">N.</Label>
                    <Input
                      value={formData.property_number}
                      onChange={(e) => handleChange('property_number', e.target.value)}
                      className="rounded-none mt-2"
                      data-testid="property-number-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.cadastralSheet}</Label>
                    <Input
                      value={formData.cadastral_sheet}
                      onChange={(e) => handleChange('cadastral_sheet', e.target.value)}
                      className="rounded-none mt-2"
                      data-testid="property-sheet-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.cadastralParcel}</Label>
                    <Input
                      value={formData.cadastral_parcel}
                      onChange={(e) => handleChange('cadastral_parcel', e.target.value)}
                      className="rounded-none mt-2"
                      data-testid="property-parcel-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.cadastralSub}</Label>
                    <Input
                      value={formData.cadastral_sub}
                      onChange={(e) => handleChange('cadastral_sub', e.target.value)}
                      className="rounded-none mt-2"
                      data-testid="property-sub-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.cinCode}</Label>
                  <Input
                    value={formData.cin_code}
                    onChange={(e) => handleChange('cin_code', e.target.value.toUpperCase())}
                    className="rounded-none mt-2"
                    data-testid="property-cin-input"
                  />
                </div>

                {/* Sezione Stanze — solo per B&B / Affittacamere */}
                {formData.property_type !== 'appartamento' && (
                  <div className="bg-emerald-50 border-2 border-emerald-300 p-4" data-testid="rooms-setup-section">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Bed size={16} weight="bold" className="text-emerald-700" />
                        <Label className="text-sm font-black uppercase tracking-wider text-emerald-800">
                          Stanze · {formData.rooms_setup?.length || 0}
                        </Label>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleChange('rooms_setup', [...(formData.rooms_setup || []), { name: '', max_occupancy: 2 }])}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-black uppercase tracking-wider"
                        data-testid="add-room-row"
                      >
                        <Plus size={11} weight="bold" /> Aggiungi stanza
                      </button>
                    </div>
                    <p className="text-[11px] text-zinc-600 mb-3 leading-snug">
                      Inserisci nome personalizzato (es. "Camera Vista Mare") e capienza massima (n° letti).
                      Saranno usate nelle pulizie per assegnare turni alle singole stanze.
                    </p>
                    {(!formData.rooms_setup || formData.rooms_setup.length === 0) && (
                      <p className="text-xs text-zinc-500 italic">Nessuna stanza · clicca "Aggiungi stanza" per iniziare.</p>
                    )}
                    <div className="space-y-2">
                      {(formData.rooms_setup || []).map((rm, idx) => (
                        <div key={idx} className="flex items-center gap-2" data-testid={`room-row-${idx}`}>
                          <Input
                            value={rm.name}
                            onChange={(e) => {
                              const next = [...formData.rooms_setup];
                              next[idx] = { ...next[idx], name: e.target.value };
                              handleChange('rooms_setup', next);
                            }}
                            placeholder={`Stanza ${idx + 1} (es. Camera Vista Mare)`}
                            className="rounded-none flex-1"
                            data-testid={`room-name-${idx}`}
                          />
                          <Input
                            type="number"
                            min={1}
                            max={10}
                            value={rm.max_occupancy}
                            onChange={(e) => {
                              const next = [...formData.rooms_setup];
                              next[idx] = { ...next[idx], max_occupancy: Number(e.target.value) || 1 };
                              handleChange('rooms_setup', next);
                            }}
                            className="rounded-none w-20"
                            data-testid={`room-occupancy-${idx}`}
                            title="Capienza massima"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const next = formData.rooms_setup.filter((_, i) => i !== idx);
                              handleChange('rooms_setup', next);
                            }}
                            className="p-2 text-zinc-400 hover:text-rose-600"
                            data-testid={`remove-room-${idx}`}
                            aria-label="Rimuovi stanza"
                          >
                            <Trash size={14} weight="bold" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.maxOccupants}</Label>
                    <Input
                      type="number"
                      min={1}
                      max={20}
                      value={formData.max_occupants}
                      onChange={(e) => handleChange('max_occupants', parseInt(e.target.value) || 1)}
                      className="rounded-none mt-2"
                      data-testid="property-occupants-input"
                    />
                  </div>
                  <div className="flex items-end">
                    <div className="flex items-center gap-3 p-3 border border-zinc-200 w-full">
                      <Switch
                        checked={formData.pets_allowed}
                        onCheckedChange={(checked) => handleChange('pets_allowed', checked)}
                        data-testid="property-pets-switch"
                      />
                      <Label className="text-sm font-medium text-zinc-700">{t.petsAllowed}</Label>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">
                    Foro competente
                  </Label>
                  <Input
                    value={formData.foro_competente}
                    onChange={(e) => handleChange('foro_competente', e.target.value)}
                    placeholder="es. Oristano (tribunale provinciale, non comunale)"
                    className="rounded-none mt-2"
                    data-testid="property-foro-input"
                  />
                  <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed">
                    Il foro competente è generalmente il tribunale del capoluogo di provincia
                    (es. immobile a Bosa → foro Oristano). Lasciando vuoto, sui contratti
                    apparirà "Tribunale di __________" da completare a mano.
                  </p>
                </div>

                {/* iCal sync section — only in edit mode */}
                {editingProperty && (
                  <IcalSection
                    property={editingProperty}
                    onChanged={fetchProperties}
                  />
                )}

                <div className="flex gap-4 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className="flex-1 rounded-none border-zinc-300"
                  >
                    Annulla
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="save-property-button"
                  >
                    {saving ? 'Salvataggio...' : (editingProperty ? 'Aggiorna' : 'Salva')}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Properties List */}
        {loading ? (
          <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">
            Caricamento...
          </div>
        ) : properties.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-12 text-center">
            <House size={48} className="text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500 mb-4">Nessun alloggio registrato</p>
            <p className="text-sm text-zinc-400 mb-6">
              Aggiungi le tue strutture turistiche per creare contratti
            </p>
            <Button
              onClick={handleNew}
              className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider"
            >
              Aggiungi il primo alloggio
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {properties.map((property) => (
              <div 
                key={property.id} 
                className="bg-white border border-zinc-200 p-4 sm:p-6 flex flex-col sm:flex-row sm:items-start justify-between gap-4"
              >
                <div className="flex gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-zinc-100 flex items-center justify-center flex-shrink-0">
                    <House size={22} className="text-zinc-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-zinc-900 text-base sm:text-lg">{property.name}</h3>
                      {property.property_type && (
                        <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 ${
                          property.property_type === 'appartamento' ? 'bg-blue-100 text-blue-700' :
                          property.property_type === 'bnb' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-violet-100 text-violet-700'
                        }`}>
                          {property.property_type === 'bnb' ? 'B&B' : property.property_type}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-zinc-500 mt-1">
                      <MapPin size={14} className="flex-shrink-0" />
                      <span className="truncate">{property.property_address}, {property.property_number} - {property.property_city} ({property.property_province})</span>
                    </div>
                    <div className="flex flex-wrap gap-3 sm:gap-4 mt-2 text-xs text-zinc-400">
                      <span>CIN: {property.cin_code || '-'}</span>
                      <span>Max: {property.max_occupants} ospiti</span>
                      <span>Animali: {property.pets_allowed ? 'Si' : 'No'}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(property)}
                    className="p-2 hover:bg-zinc-100 transition-colors"
                    title="Modifica"
                    data-testid={`edit-property-${property.id}`}
                  >
                    <PencilSimple size={18} className="text-zinc-600" />
                  </button>
                  <button
                    onClick={() => handleDelete(property.id)}
                    className="p-2 hover:bg-red-50 transition-colors"
                    title="Elimina"
                    data-testid={`delete-property-${property.id}`}
                  >
                    <Trash size={18} className="text-red-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
